import { useEffect, useState } from "react";

import dayjs from "dayjs";

import Card from "react-bootstrap/Card";
import Col from "react-bootstrap/Col";
import Row from "react-bootstrap/Row";
import { FaUser } from "react-icons/fa";

import { useLayout } from "@/../_metronic/layout/core";
import { useCurrentAccount } from "@/hooks/useCurrentAccount";
import { useEsus } from "@/hooks/useEsus";
import { Paginated } from "@/@types/generics/paginated";
import { useDebounce } from "@/hooks/useDebounce";
import {
	getTripleViralCitizensVaccinationsPaginated,
	getTripleViralResume,
	TripleViralResumeType,
} from "@/services/esus/vaccinationService";
import { fetchWithCache } from "@/utils/fetchWithCache";
import { getExpirationDateMidnight } from "@/utils/getExpirationDateMidnight";
import { exportExcel } from "@/utils/exports";
import { GenericObject } from "@/@types/generics/genericals";
import { VaccinationStatusMessage } from "@/utils/enums/VaccinationStatusEnum";

import { PaginationLinks } from "@/components/PaginationLinks";
import { NoRecordsFeedback } from "@/components/NoRecordsFeedback";
import { TripleViralCitizenVaccinationsModal } from "./TripleViralCitizenVaccinationsModal";
import { CitizenVaccinations, TripleViralCitizensTable } from "./TripleViralCitizensTable";
import { TripleViralCitizensFilters } from "./TripleViralCitizensFilters";
import { TripleViralResumes } from "./TripleViralResumes";
import { TripleViralCitizensResumeFilters } from "./TripleViralResumes/TripleViralCitizensResumeFilters";

export function TripleViralManagement() {
	const { setTitle } = useLayout();
	const { uf, ibgeCode, cnes } = useCurrentAccount();
	const { healthTeams, units } = useEsus();
	const [fullName, setFullName] = useState("");
	const debouncedFullName = useDebounce(fullName, 1000);
	const [vaccinationStatus, setVaccinationStatus] = useState("");
	const [startDate, setStartDate] = useState(dayjs().subtract(1, "month").format("YYYY-MM-DD"));
	const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
	const [selectedCnes, setSelectedCnes] = useState("");
	const [selectedIne, setSelectedIne] = useState("");
	const [showLoadingScreen, setShowLoadingScreen] = useState(false);
	const [paginatedCitizensVaccinations, setPaginatedCitizensVaccinations] = useState<
		Paginated<CitizenVaccinations>
	>({} as Paginated<CitizenVaccinations>);
	const [pageNumber, setPageNumber] = useState(0);
	const [isLoading, setIsLoading] = useState(false);
	const [selectedCitizenVaccinations, setSelectedCitizenVaccinations] =
		useState<CitizenVaccinations>({} as CitizenVaccinations);
	const [tripleViralResume, setTripleViralResume] = useState<TripleViralResumeType>(
		{} as TripleViralResumeType
	);
	const [showTripleViralCitizenVaccinationsModal, setShowTripleViralCitizenVaccinationsModal] =
		useState(false);

	async function handleGetTripleViralResume() {
		return await getTripleViralResume({
			uf,
			ibgeCode,
			startDate,
			endDate,
			cnes: selectedCnes || cnes,
			ine: selectedIne,
		});
	}

	async function getPaginatedTripleViralCitizensVaccinations(pageSize?: number) {
		return await getTripleViralCitizensVaccinationsPaginated({
			uf,
			ibgeCode,
			startDate,
			endDate,
			fullName,
			vaccinationStatus,
			cnes: selectedCnes || cnes,
			ine: selectedIne,
			pageNumber,
			pageSize,
		});
	}

	async function handleFilterResume() {
		const cacheKey = `[tripleViralManagement][${uf}][${ibgeCode}][${startDate}][${endDate}][${
			selectedCnes || cnes
		}][${selectedIne}]`;
		const cacheExpirationDate = getExpirationDateMidnight();
		setIsLoading(true);
		setTripleViralResume(
			await fetchWithCache(cacheKey, cacheExpirationDate, handleGetTripleViralResume)
		);
		setIsLoading(false);
		if (pageNumber !== 1) {
			setPageNumber(1);
		} else {
			handleFilter();
		}
	}

	async function handleFilter() {
		setIsLoading(true);
		setPaginatedCitizensVaccinations({} as Paginated<CitizenVaccinations>);
		setPaginatedCitizensVaccinations(await getPaginatedTripleViralCitizensVaccinations());
		setIsLoading(false);
	}

	function handleOpenTripleViralCitizenVaccinationsModal(
		citizenVaccinations: CitizenVaccinations
	) {
		setSelectedCitizenVaccinations(citizenVaccinations);
		setShowTripleViralCitizenVaccinationsModal(true);
	}

	function handleCloseTripleViralCitizenVaccinationsModal() {
		setShowTripleViralCitizenVaccinationsModal(false);
	}

	async function handleExportTable() {
		setShowLoadingScreen(true);
		const response = await getPaginatedTripleViralCitizensVaccinations(
			paginatedCitizensVaccinations.totalRecords
		);
		setShowLoadingScreen(false);

		if (Object.keys(response?.data || []).length) {
			const excelData: GenericObject[] = transformPregnantPatientsToExportExcel(
				response?.data
			);
			exportExcel(excelData, `Tabela`);
		}
	}

	function transformPregnantPatientsToExportExcel(citizensVaccinations: CitizenVaccinations[]) {
		return citizensVaccinations.map((citizenVaccination) => {
			const vaccinationStatusMessage =
				VaccinationStatusMessage[
					citizenVaccination.vaccinationStatus as keyof typeof VaccinationStatusMessage
				];

			const birthDate = citizenVaccination.citizen.birthdate
				? dayjs(citizenVaccination.citizen.birthdate).format("DD/MM/YYYY")
				: null;

			return {
				nome: citizenVaccination.citizen.fullName,
				status: vaccinationStatusMessage,
				"Data de nascimento": birthDate,
				idade: citizenVaccination.citizen.age,
				mc: citizenVaccination.citizen.area,
				D1: citizenVaccination.statusD1 ? "Sim" : "Não",
				D2: citizenVaccination.statusD2 ? "Sim" : "Não",
				DU: citizenVaccination.statusDU ? "Sim" : "Não",
			} as GenericObject;
		});
	}

	useEffect(() => {
		if (pageNumber !== 1) {
			setPageNumber(1);
		} else {
			handleFilter();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedFullName, vaccinationStatus]);

	useEffect(() => {
		if (pageNumber !== 0) {
			handleFilter();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pageNumber]);

	useEffect(() => {
		setTitle("VACINAÇÃO TRÍPLICE VIRAL EM CRIANÇAS ENTRE 1 E 2 ANOS");
		handleFilterResume();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="mx-4">
			<Row className="my-2">
				<Col>
					<Card>
						<Card.Body className="py-4">
							<TripleViralCitizensResumeFilters
								setSelectedCnes={setSelectedCnes}
								healthUnits={units}
								setSelectedIne={setSelectedIne}
								healthTeams={healthTeams}
								startDate={startDate}
								setStartDate={setStartDate}
								endDate={endDate}
								setEndDate={setEndDate}
								handleFilter={handleFilterResume}
							/>
						</Card.Body>
					</Card>
				</Col>
			</Row>
			<Row className="my-2">
				<Col>
					<TripleViralResumes tripleViralResume={tripleViralResume} />
				</Col>
			</Row>
			<Row className="my-2">
				<Col>
					<Card>
						<Card.Body>
							<TripleViralCitizensFilters
								fullName={fullName}
								setFullName={setFullName}
								setVaccinationStatus={setVaccinationStatus}
								handleFilter={handleFilter}
								handleExport={handleExportTable}
								isLoadingExport={showLoadingScreen}
							/>
							<TripleViralCitizensTable
								citizensVaccinations={paginatedCitizensVaccinations.data}
								handleViewCitizenVaccinations={
									handleOpenTripleViralCitizenVaccinationsModal
								}
								isLoading={isLoading}
							/>
						</Card.Body>
						<Card.Footer>
							{paginatedCitizensVaccinations.totalRecords ? (
								<PaginationLinks
									itemsPerPage={paginatedCitizensVaccinations.pageSize}
									totalPages={paginatedCitizensVaccinations.totalRecords}
									changeSelectedPage={setPageNumber}
									pageNumber={pageNumber || 0}
								/>
							) : (
								!isLoading && (
									<NoRecordsFeedback
										message={"Nenhum cidadão encontrado."}
										icon={<FaUser />}
									/>
								)
							)}
						</Card.Footer>
					</Card>
				</Col>
			</Row>
			<TripleViralCitizenVaccinationsModal
				show={showTripleViralCitizenVaccinationsModal}
				onHide={handleCloseTripleViralCitizenVaccinationsModal}
				citizenVaccinations={selectedCitizenVaccinations}
			/>
		</div>
	);
}
